import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { getDraftRunSetup } from '@api/appBuilder';
import {
  getAppRunSetup,
  type AppRunExecution,
  type AppRunDetail,
  type AppRunSetup,
  type AppRunsOn,
} from '@api/apps';
import { setStoredApprovalMode } from '@utils/approvalMode';
import type { RunSetupTarget } from '../appFrame/runHost';
import { RUN_SELECTION_STORAGE_KEY, RUN_SETUP_POLL_MS } from './runSetup.constants';
import { blockerMessage, blockerOf, resolveSelection } from './runSetup';

type StoredSelections = Record<string, Partial<AppRunExecution>>;

const readStored = (): StoredSelections => {
  try {
    const raw = localStorage.getItem(RUN_SELECTION_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? (parsed as StoredSelections) : {};
  } catch {
    return {};
  }
};

const store = (appKey: string, selection: Partial<AppRunExecution>): void => {
  try {
    localStorage.setItem(RUN_SELECTION_STORAGE_KEY, JSON.stringify({ ...readStored(), [appKey]: selection }));
  } catch {
    // A choice that cannot be remembered is still used for this run.
  }
};

const setupOf = (appKey: string, run: AppRunDetail | null, target: RunSetupTarget): Promise<AppRunSetup> => {
  if (run) return getAppRunSetup({ runId: run.runId });
  return target === 'draft' ? getDraftRunSetup(appKey) : getAppRunSetup({ appKey });
};

/**
 * Where and with which software the person runs an app that may run on their
 * own computer, and whether that runner can take it right now. The choice is
 * remembered per app, so the composer, a rerun and the next visit all start
 * from it. `runsOn` is the app's as it is now, which is what the server judges
 * a new run by, not the one a finished run was started on.
 */
export const useAppRunSetup = (
  appKey: string | undefined,
  runsOn: AppRunsOn | undefined,
  run: AppRunDetail | null,
  target: RunSetupTarget,
) => {
  const intl = useIntl();
  const runsLocally = Boolean(runsOn && runsOn !== 'CLOUD');
  const enabled = Boolean(appKey) && runsLocally && !run?.readOnly;
  const { data: setup } = useQuery({
    queryKey: ['apps', 'run-setup', appKey, run?.runId ?? target, runsOn],
    queryFn: () => setupOf(appKey as string, run, target),
    enabled,
    refetchInterval: RUN_SETUP_POLL_MS,
  });
  const [pickedByApp, setPickedByApp] = useState<StoredSelections>(readStored);
  const picked = useMemo(() => (appKey ? (pickedByApp[appKey] ?? {}) : {}), [appKey, pickedByApp]);

  const selection = useMemo(() => (enabled && setup ? resolveSelection(setup, picked) : null), [enabled, setup, picked]);
  const blocker = useMemo(() => (setup && selection ? blockerOf(setup, selection) : null), [setup, selection]);

  const select = useCallback(
    (patch: Partial<AppRunExecution>) => {
      if (!appKey) return;
      store(appKey, { ...picked, ...patch });
      setPickedByApp((current) => ({ ...current, [appKey]: { ...current[appKey], ...patch } }));
      if (patch.approvalMode) setStoredApprovalMode(null, patch.approvalMode);
    },
    [appKey, picked],
  );

  return {
    setup: enabled ? (setup ?? null) : null,
    selection,
    blocker,
    blockedReason: blocker ? blockerMessage(blocker, intl) : null,
    select,
  };
};

export type AppRunSetupState = ReturnType<typeof useAppRunSetup>;
